import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getCombinedPortfolio, getPortfolioByCountry, getPortfolioByAccount, getPortfolioScore, analyzePortfolio, getPortfolioPerformance, getPriceOnlyPerformance, getAccounts, getRuleViolations, triggerPortfolioRefresh, refreshPortfolioQuotes, getRefreshStatus, getHoldingScores, getPortfolioZones, getPortfolioNews, getLivePrices, getWatchlist, removeFromWatchlist, getPnlBreakdown, getDividendYield } from "../lib/api";
import { TrendingUp, TrendingDown, IndianRupee, DollarSign, Briefcase, Loader2, ArrowUpDown, ArrowUp, ArrowDown, ShieldAlert, AlertTriangle, RefreshCw, Newspaper, ExternalLink, Star, X, ChevronDown, ChevronRight, Download } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, BarChart, Bar, ReferenceLine, ComposedChart, Line } from "recharts";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import PortfolioChat from "../components/PortfolioChat";
import { MarketPulsePanel, MorningSummaryCard, EveningWrapCard, WeekendReviewCard, ClosingBellCard } from "../components/NewsPulse";
import CorporateActionsCard from "../components/CorporateActionsCard";
import PassiveIncomeCard from "../components/PassiveIncomeCard";
import TopMoversCard from "../components/TopMoversCard";
import TickerTape from "../components/TickerTape";
import { cacheGet, cacheSet, cacheClearAll, timeAgo } from "../lib/persistentCache";

const PIE_COLORS = ["#007AFF", "#10B981", "#F59E0B", "#FF3B30", "#64748B", "#8B5CF6", "#EC4899", "#06B6D4"];
const ACCT_COLORS = ["#00D4FF", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#FF6B6B", "#06B6D4"];
const STRING_SORT_KEYS = new Set(["tradingsymbol", "account", "sector"]);

const isMutualFundHolding = (holding) =>
  holding?.asset_class === "Mutual Fund" || holding?.broker === "mutual_fund";

const holdingInvestedValue = (holding) =>
  Number(holding?.invested_value || (holding?.average_price * holding?.quantity) || 0);

const holdingUsesUsd = (holding) =>
  holding?.currency === "USD" || holding?.country === "US";

const holdingsMetricsInInr = (holdings, usdInrRate = 1) => {
  const metrics = (holdings || []).reduce((totals, holding) => {
    const fx = holdingUsesUsd(holding) ? Number(usdInrRate || 1) : 1;
    totals.total_invested += holdingInvestedValue(holding) * fx;
    totals.total_value += Number(holding?.current_value || 0) * fx;
    return totals;
  }, { total_invested: 0, total_value: 0 });
  metrics.total_pnl = metrics.total_value - metrics.total_invested;
  metrics.total_pnl_pct = metrics.total_invested
    ? (metrics.total_pnl / metrics.total_invested) * 100
    : 0;
  metrics.total_stocks = holdings?.length || 0;
  return metrics;
};

export default function DashboardPage({ onStockClick }) {
  const [globalPortfolio, setGlobalPortfolio] = useState(null); // Always combined
  const [portfolio, setPortfolio] = useState(null); // Filtered by view
  const [score, setScore] = useState(null);
  const [perf, setPerf] = useState([]);
  const [perfAccounts, setPerfAccounts] = useState([]);
  const [perfRange, setPerfRange] = useState("1M");
  const [perfMode, setPerfMode] = useState("value"); // "value" | "price_only"
  const [perfSummary, setPerfSummary] = useState(null);
  const [dayPnl, setDayPnl] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [analysis, setAnalysis] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [view, setView] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [sortKey, setSortKey] = useState("current_value");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [ruleViolations, setRuleViolations] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [holdingScores, setHoldingScores] = useState({});
  const [zones, setZones] = useState(null);
  const [portfolioNews, setPortfolioNews] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [pnlPopupOpen, setPnlPopupOpen] = useState(false);
  const [pnlBreakdown, setPnlBreakdown] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null); // wall-clock when fresh data arrived
  const [holdingsOpen, setHoldingsOpen] = useState(false); // collapsed by default
  const [hydratedFromCache, setHydratedFromCache] = useState(false);
  const [showNifty, setShowNifty] = useState(true);
  const [refreshSources, setRefreshSources] = useState({}); // per-source health from /refresh-status
  const [dividendYields, setDividendYields] = useState({}); // {symbol: {yield_pct, ttm_dividend, ...}}

  // On first mount: hydrate critical state from localStorage so the dashboard
  // renders last-known-good data instantly — even if the backend is slow or
  // the first fetch fails. Fresh fetch kicks in right after.
  useEffect(() => {
    if (hydratedFromCache) return;
    const cachedCombined = cacheGet("combined");
    const cachedFiltered = cacheGet("filtered");
    const cachedAccounts = cacheGet("accounts");
    const cachedScore = cacheGet("score");
    if (cachedCombined?.value) {
      setGlobalPortfolio(cachedCombined.value);
      setLastSyncedAt(cachedCombined.savedAt);
    }
    if (cachedFiltered?.value) setPortfolio(cachedFiltered.value);
    if (cachedAccounts?.value) setAccounts(cachedAccounts.value);
    if (cachedScore?.value) setScore(cachedScore.value);
    setHydratedFromCache(true);
    // Fetch dividend yields (cheap — reads from cached collection, no NSE calls)
    getDividendYield().then(r => setDividendYields(r.data.yields || {})).catch(() => {});
  }, [hydratedFromCache]);


  // Load watchlist
  const loadWatchlist = useCallback(async () => {
    try { const { data } = await getWatchlist(); setWatchlist(data.watchlist || []); } catch {}
  }, []);

  useEffect(() => { loadWatchlist(); }, [loadWatchlist]);

  // In-flight guard — prevents auto-polls from stacking up on slow networks
  const isLoadingRef = React.useRef(false);

  const loadData = useCallback(async () => {
    if (isLoadingRef.current) return; // skip if previous load still in-flight
    isLoadingRef.current = true;
    setLoadError(null);
    let hasAnyData = false;

    // Determine filtered fetch. Avoid double-firing when filter == combined (default state)
    let filteredPromise;
    if (accountFilter !== "all") {
      filteredPromise = getPortfolioByAccount(accountFilter);
    } else if (view === "india" || view === "us") {
      filteredPromise = getPortfolioByCountry(view === "india" ? "India" : "US");
    } else if (view === "mf") {
      filteredPromise = getPortfolioByCountry("India"); // MFs handled in filter
    } else {
      filteredPromise = null; // we'll reuse combined below
    }

    const perfPromise = perfMode === "price_only" ? getPriceOnlyPerformance(perfRange) : getPortfolioPerformance(perfRange);

    // Run all critical reads in parallel — DB-cached so they return fast
    const combinedPromise = getCombinedPortfolio();
    const [gRes, pRes, accRes, sRes, perfRes] = await Promise.allSettled([
      combinedPromise,
      filteredPromise || combinedPromise,  // reuse same promise (single network call) when default
      getAccounts(),
      getPortfolioScore(),
      perfPromise,
    ]);

    if (gRes.status === "fulfilled") {
      setGlobalPortfolio(gRes.value.data);
      cacheSet("combined", gRes.value.data);
      hasAnyData = true;
    } else {
      const err = gRes.reason;
      console.warn("[loadData] combined failed:", err?.code, err?.message, err?.response?.status);
    }
    if (pRes.status === "fulfilled") {
      setPortfolio(pRes.value.data);
      cacheSet("filtered", pRes.value.data);
      if (pRes.value.data?.refresh_status) {
        setRefreshing(pRes.value.data.refresh_status.in_progress);
        setLastRefresh(pRes.value.data.refresh_status.last_refresh);
        if (pRes.value.data.refresh_status.sources) {
          setRefreshSources(pRes.value.data.refresh_status.sources);
        }
      }
    }
    if (accRes.status === "fulfilled") {
      setAccounts(accRes.value.data.accounts || []);
      cacheSet("accounts", accRes.value.data.accounts || []);
    }
    if (sRes.status === "fulfilled") {
      setScore(sRes.value.data);
      cacheSet("score", sRes.value.data);
    }
    if (perfRes.status === "fulfilled") {
      if (perfMode === "price_only") {
        const series = (perfRes.value.data.series || []).map(p => {
          const out = { date: p.date, total: p.total_pct };
          if (p.nifty_pct !== undefined) out.nifty_pct = p.nifty_pct;
          (perfRes.value.data.accounts || []).forEach(a => { out[a] = p[a] ?? 0; });
          return out;
        });
        setPerf(series);
        // Normalise to {name} shape so downstream chart code is uniform with value-mode
        setPerfAccounts((perfRes.value.data.accounts || []).map(a => ({ name: a })));
        setPerfSummary(perfRes.value.data.summary);
        setDayPnl(null);
      } else {
        setPerf(perfRes.value.data.performance || []);
        setPerfAccounts(perfRes.value.data.accounts || []);
        setPerfSummary(null);
        const liveSummary = gRes.status === "fulfilled" ? gRes.value.data?.summary : null;
        setDayPnl(previous => {
          if (liveSummary?.day_pnl != null) {
            // Keep the last complete combined figure while either market's
            // quote set is still loading. A partial India-only total must never
            // replace a complete India + US reading.
            if (liveSummary.day_pnl_complete === false) return previous;
            return {
              pnl: liveSummary.day_pnl,
              pct: liveSummary.day_pnl_pct,
              india: liveSummary.day_pnl_india,
              us: liveSummary.day_pnl_us,
              quoted: liveSummary.day_pnl_quoted_holdings,
              missing: liveSummary.day_pnl_missing_holdings,
            };
          }
          return previous || (perfRes.value.data.day_pnl != null
            ? { pnl: perfRes.value.data.day_pnl, pct: perfRes.value.data.day_pnl_pct }
            : null);
        });
      }
    }

    // Non-critical (fire-and-forget)
    getRuleViolations().then(r => setRuleViolations(r.data)).catch(() => {});
    getHoldingScores().then(r => setHoldingScores(r.data?.scores || {})).catch(() => {});
    getPortfolioZones().then(r => setZones(r.data)).catch(() => {});
    getPortfolioNews().then(r => setPortfolioNews(r.data?.news || [])).catch(() => {});

    if (!hasAnyData) setLoadError("Could not load portfolio data.");
    else { setRetryCount(0); setLastSyncedAt(Date.now()); }
    setLoading(false);
    isLoadingRef.current = false;
  }, [view, accountFilter, perfRange, perfMode]);

  useEffect(() => {
    loadData();
    // Market-aware polling: 60s during India market hours (9:15-15:30 IST, Mon-Fri), 5min outside.
    // Skip polls when the tab is hidden — saves backend load and avoids the page-jumping reflow
    // when the user comes back to the tab.
    const isMarketOpen = () => {
      const now = new Date();
      const ist = new Date(now.getTime() + (now.getTimezoneOffset() + 330) * 60000);
      const day = ist.getDay();
      if (day === 0 || day === 6) return false;
      const minutes = ist.getHours() * 60 + ist.getMinutes();
      return minutes >= (9 * 60 + 15) && minutes <= (15 * 60 + 30);
    };
    const intervalMs = isMarketOpen() ? 60000 : 300000;
    const i = setInterval(() => {
      if (document.hidden) return;     // don't poll background tabs
      if (isLoadingRef.current) return; // don't stack polls
      loadData();
    }, intervalMs);
    return () => clearInterval(i);
  }, [loadData]);

  useEffect(() => {
    if (!loadError || retryCount >= 6) return;
    // Exponential backoff: 5s, 10s, 20s, 40s, 60s, 60s — instead of constant 5s
    const delays = [5000, 10000, 20000, 40000, 60000, 60000];
    const delay = delays[Math.min(retryCount, delays.length - 1)];
    const timer = setTimeout(() => {
      if (isLoadingRef.current) return; // skip if a regular poll is in-flight
      setRetryCount(c => c + 1);
      loadData();
    }, delay);
    return () => clearTimeout(timer);
  }, [loadError, retryCount, loadData]);

  useEffect(() => {
    if (!refreshing) return;
    const poll = setInterval(async () => {
      try {
        const { data } = await getRefreshStatus();
        if (!data.in_progress) { setRefreshing(false); loadData(); clearInterval(poll); }
      } catch {}
    }, 3000);
    return () => clearInterval(poll);
  }, [refreshing, loadData]);

  const handleRefresh = async () => { setRefreshing(true); try { await triggerPortfolioRefresh(); await loadData(); } catch { setRefreshing(false); } };

  // Full Refresh — nukes localStorage cache + in-memory state, then re-fetches everything fresh.
  // Useful when the regular polling/retry keeps returning stale data (cold-start pod, transient 5xx).
  const [fullRefreshing, setFullRefreshing] = useState(false);
  const handleFullRefresh = async () => {
    setFullRefreshing(true);
    cacheClearAll();
    // Wipe in-memory state so the stale cards don't flash back during the fetch
    setGlobalPortfolio(null);
    setPortfolio(null);
    setScore(null);
    setPerf([]);
    setPerfAccounts([]);
    setPerfSummary(null);
    setLoadError(null);
    setRetryCount(0);
    setLoading(true);
    try {
      // A browser-cache refresh is not a market-data refresh. Force the
      // backend quote cache to obtain a new same-session price/previous-close
      // pair before rebuilding the dashboard.
      await refreshPortfolioQuotes();
      await loadData();
    } finally {
      setFullRefreshing(false);
    }
  };
  const handleAnalyze = async () => { setAnalyzing(true); try { const res = await analyzePortfolio(); setAnalysis(res.data.analysis); } catch { setAnalysis("Analysis failed."); } finally { setAnalyzing(false); } };

  const handleOpenPnlPopup = async () => {
    setPnlPopupOpen(true);
    if (!pnlBreakdown) {
      try { const { data } = await getPnlBreakdown(); setPnlBreakdown(data); } catch { setPnlBreakdown({ error: true }); }
    }
  };

  const gs = globalPortfolio?.summary; // Global summary (always combined)
  const s = view === "mf" ? null : portfolio?.summary; // Filtered summary (MF handled below)

  const holdings = useMemo(() => {
    const all = portfolio?.holdings || [];
    if (view === "mf") return all.filter(isMutualFundHolding);
    // The India tab is India listed equity. Mutual funds have their own tab.
    if (view === "india") return all.filter(h => !isMutualFundHolding(h));
    return all;
  }, [portfolio, view]);
  const sectors = portfolio?.sectors || [];

  // India and MF share the country endpoint, so compute their summaries from
  // the explicitly scoped holdings instead of displaying the country-wide sum.
  // Some MFs from INDmoney report invested_value=0 but their average_price IS the
  // actual cost basis, so avg*qty is a reliable fallback.
  const filteredSummary = useMemo(() => {
    if ((view !== "mf" && view !== "india") || !portfolio) return portfolio?.summary;
    const { total_invested, total_value, total_pnl, total_pnl_pct } = holdingsMetricsInInr(
      holdings,
      globalPortfolio?.summary?.usd_inr_rate,
    );
    return {
      total_value: Math.round(total_value * 100) / 100,
      total_invested: Math.round(total_invested * 100) / 100,
      total_pnl: Math.round(total_pnl * 100) / 100,
      total_pnl_pct,
      total_stocks: holdings.length,
    };
  }, [view, portfolio, holdings, globalPortfolio]);

  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(key); setSortDir(STRING_SORT_KEYS.has(key) ? "asc" : "desc"); } };
  const sortedHoldings = useMemo(() => {
    const isStringSort = STRING_SORT_KEYS.has(sortKey);
    const getVal = (h) => {
      if (sortKey === "score") {
        const sc = holdingScores[h.tradingsymbol];
        return sc?.avg_score ?? -1;
      }
      if (sortKey === "account") return (h.account_name || h.broker || "").toString();
      if (sortKey === "tradingsymbol") return (h.tradingsymbol || "").toString();
      if (sortKey === "sector") return (h.sector || "").toString();
      return h[sortKey] ?? 0;
    };
    return [...holdings].sort((a, b) => {
      const av = getVal(a), bv = getVal(b);
      if (isStringSort) {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [holdings, sortKey, sortDir, holdingScores]);

  const globalMfValue = useMemo(() => {
    const all = globalPortfolio?.holdings || [];
    return all.filter(h => h.asset_class === "Mutual Fund" || h.broker === "mutual_fund").reduce((sum, h) => sum + (h.current_value || 0), 0);
  }, [globalPortfolio]);

  // Filter performance chart accounts
  const filteredPerfAccounts = useMemo(() => {
    if (accountFilter !== "all") {
      const acc = accounts.find(a => a.id === accountFilter);
      return acc ? perfAccounts.filter(pa => pa.name === acc.account_name) : perfAccounts;
    }
    if (view === "india") return perfAccounts.filter(pa => { const acc = accounts.find(a => a.account_name === pa.name); return acc && ["zerodha", "icici", "angel"].includes(acc.broker); });
    if (view === "us") return perfAccounts.filter(pa => { const acc = accounts.find(a => a.account_name === pa.name); return acc && acc.broker === "us_stocks"; });
    if (view === "mf") return perfAccounts.filter(pa => { const acc = accounts.find(a => a.account_name === pa.name); return acc && acc.broker === "mutual_fund"; });
    return perfAccounts;
  }, [perfAccounts, view, accountFilter, accounts]);

  // Sort accounts: India brokers first, then US
  const sortedAccounts = useMemo(() => {
    const order = { icici: 1, zerodha: 2, angel: 3, manual: 4, mutual_fund: 5, us_stocks: 6 };
    return [...accounts].sort((a, b) => (order[a.broker] || 9) - (order[b.broker] || 9));
  }, [accounts]);
  const indiaAccounts = sortedAccounts.filter(a => ["zerodha", "icici", "angel", "manual", "mutual_fund"].includes(a.broker));
  const usAccounts = sortedAccounts.filter(a => a.broker === "us_stocks");

  const isFiltered = view !== "all" || accountFilter !== "all";
  const filterLabel = accountFilter !== "all" ? accounts.find(a => a.id === accountFilter)?.account_name : view === "india" ? "India" : view === "us" ? "US" : view === "mf" ? "Mutual Funds" : "";

  if (loading && !globalPortfolio) return <div className="flex items-center justify-center h-screen"><Loader2 className="animate-spin text-[var(--brand)]" size={32} /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-5" data-testid="dashboard-page">
      {/* Live ticker tape — always-on price banner */}
      <TickerTape
        onStockClick={onStockClick}
        fallbackHoldings={globalPortfolio?.holdings || portfolio?.holdings || []}
      />

      {loadError && !globalPortfolio && (
        <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3" data-testid="load-error-banner">
          <div className="flex items-center gap-2"><AlertTriangle size={16} className="text-amber-500" /><span className="text-sm text-amber-400">{retryCount < 6 ? `Connecting... (attempt ${retryCount + 1} of 6)` : "Unable to load data — backend may be down."}</span></div>
          <Button variant="outline" size="sm" onClick={() => { setRetryCount(0); setLoading(true); loadData(); }} className="text-xs border-amber-500/30"><RefreshCw size={14} className="mr-1" /> Retry</Button>
        </div>
      )}
      {loadError && globalPortfolio && (
        <div className="inline-flex items-center gap-2 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1 text-[10px] text-amber-300" data-testid="load-error-pill">
          <AlertTriangle size={11} /> Refresh failed — showing cached data{lastSyncedAt ? ` from ${timeAgo(lastSyncedAt)}` : ""}
          <button onClick={() => { setRetryCount(0); loadData(); }} className="underline hover:text-amber-200">retry</button>
        </div>
      )}

      {/* Per-source health pill — shows when ANY source failed/partial in the last refresh */}
      {(() => {
        const sources = refreshSources || {};
        const failed = Object.entries(sources).filter(([_, s]) => s.status === "failed" || s.status === "partial");
        if (!failed.length) return null;
        return (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-md px-3 py-2 text-[11px] text-amber-200" data-testid="refresh-source-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium mb-1">Partial refresh — some sources failed</div>
                <div className="space-y-0.5">
                  {failed.map(([name, s]) => (
                    <div key={name} className="flex items-center gap-2 font-mono text-[10px]" data-testid={`refresh-source-${name}`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.status === "failed" ? "bg-red-400" : "bg-amber-400"}`} />
                      <span className="text-amber-100 min-w-[140px] truncate">{name}</span>
                      <span className="text-[var(--text-muted)]">{s.status} · {s.duration_ms}ms</span>
                      {s.error && <span className="text-amber-300/80 truncate max-w-[400px]">— {s.error}</span>}
                    </div>
                  ))}
                </div>
                <div className="text-[var(--text-muted)] text-[10px] mt-1">Other sources updated successfully. Showing cached values for the failed ones.</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Header + Filters */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Equity Dashboard</h1>
            <p className="text-sm text-[var(--text-muted)]">{(globalPortfolio?.holdings || []).length} holdings across {accounts.length} accounts</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {lastSyncedAt && (
              <span className="text-[10px] text-[var(--text-muted)] font-mono" data-testid="last-synced-badge">
                Last synced {new Date(lastSyncedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={handleFullRefresh} disabled={fullRefreshing} className="gap-1.5 border-amber-500/30 text-xs hover:border-amber-400/60 text-amber-300 hover:text-amber-200" title="Clear cache and re-fetch everything fresh" data-testid="full-refresh-btn">
              <RefreshCw size={14} className={fullRefreshing ? "animate-spin" : ""} />{fullRefreshing ? "Refreshing..." : "Full Refresh"}
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="gap-1.5 border-white/10 text-xs" data-testid="refresh-portfolio-btn">
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />{refreshing ? "Syncing..." : "Sync Brokers"}
            </Button>
          </div>
        </div>

        {/* Filter Row 1: Category */}
        <div className="flex flex-wrap gap-2 items-center" data-testid="view-toggle">
          <div className="flex gap-1 bg-[var(--bg-surface)] border border-white/10 rounded-md p-0.5">
            {[["all", "Combined"], ["india", "India"], ["us", "US"], ["mf", "Mutual Funds"]].map(([k, l]) => (
              <button key={k} onClick={() => { setView(k); setAccountFilter("all"); setPortfolio(null); }}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${view === k && accountFilter === "all" ? "bg-[var(--brand)] text-white" : "text-[var(--text-secondary)] hover:text-white"}`}
                data-testid={`view-${k}`}>{l}</button>
            ))}
          </div>
        </div>

        {/* Filter Row 2: Individual accounts — India first, then US */}
        {accounts.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {indiaAccounts.map(acc => (
              <button key={acc.id} onClick={() => { setAccountFilter(acc.id); setView("all"); setPortfolio(null); }}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-md border transition-colors ${accountFilter === acc.id ? "bg-[var(--brand)] text-white border-transparent" : "border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-white/20"}`}
                data-testid={`view-account-${acc.id}`}>{acc.account_name}</button>
            ))}
            {usAccounts.length > 0 && <span className="w-px h-6 bg-white/10 self-center mx-1" />}
            {usAccounts.map(acc => (
              <button key={acc.id} onClick={() => { setAccountFilter(acc.id); setView("all"); setPortfolio(null); }}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-md border transition-colors ${accountFilter === acc.id ? "bg-[var(--brand)] text-white border-transparent" : "border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-white/20"}`}
                data-testid={`view-account-${acc.id}`}>{acc.account_name}</button>
            ))}
          </div>
        )}
      </div>

      {/* GLOBAL KPI Cards — Equity-focused */}
      {gs && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KPICard label="Total Equity (Today)" value={fmtINR(gs.total_value)} icon={<Briefcase size={16} />} testId="kpi-equity"
              sub={`India + MF + US · ${gs.total_stocks} holdings`} positive />
            <KPICard label="Total P&L" value={fmtINR(gs.total_pnl)} sub={`${gs.total_pnl_pct >= 0 ? "+" : ""}${gs.total_pnl_pct}% · Click for breakdown`}
              icon={gs.total_pnl >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />} positive={gs.total_pnl >= 0} testId="kpi-pnl" onClick={handleOpenPnlPopup} />
            <KPICard label="Combined Today's P&L" value={dayPnl ? fmtINR(dayPnl.pnl) : "--"}
              sub={dayPnl
                ? `${dayPnl.pnl >= 0 ? "+" : ""}${dayPnl.pct}% · India ${fmtINR(dayPnl.india)} + US ${fmtINR(dayPnl.us)}${dayPnl.missing ? ` · ${dayPnl.missing} unavailable` : ""}`
                : "Waiting for complete India + US quote sets"}
              icon={dayPnl && dayPnl.pnl >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />} positive={dayPnl ? dayPnl.pnl >= 0 : undefined} testId="kpi-day-pnl" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KPICard label="India Equity" value={fmtINR(gs.india_value)} icon={<IndianRupee size={16} />} testId="kpi-india"
              sub={gs.total_value ? `${((gs.india_value / gs.total_value) * 100).toFixed(1)}% of equity` : undefined} />
            <KPICard label="Mutual Funds" value={fmtINR(globalMfValue)} icon={<Briefcase size={16} />} testId="kpi-mf"
              sub={gs.total_value ? `${((globalMfValue / gs.total_value) * 100).toFixed(1)}% of equity` : undefined} />
            <KPICard label="US Stocks" value={gs.us_value_usd > 0 ? `$${gs.us_value_usd.toLocaleString(undefined,{maximumFractionDigits:0})}` : "--"} icon={<DollarSign size={16} />} testId="kpi-us"
              sub={gs.us_value_inr > 0 ? `~ ${fmtINR(gs.us_value_inr)} · ${gs.total_value ? ((gs.us_value_inr / gs.total_value) * 100).toFixed(1) : 0}% of equity` : undefined} />
          </div>
        </>
      )}

      {/* FILTERED ACCOUNT STRIP — Shows only when a filter is active. Placed
          above the Holdings table so the user sees totals first. */}
      {isFiltered && !(s || filteredSummary) && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-3" data-testid="account-strip-loading">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-[var(--brand)] font-semibold">{filterLabel}</span>
            <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1.5"><Loader2 size={10} className="animate-spin" /> Loading…</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-2">
            {[..."abcde"].map((k) => (
              <div key={k}>
                <div className="text-[9px] uppercase text-[var(--text-muted)]/50 tracking-wider">—</div>
                <div className="font-mono text-lg text-[var(--text-muted)]/30">···</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {isFiltered && (s || filteredSummary) && (() => {
        const ss = (view === "mf" || view === "india") ? filteredSummary : s;
        if (!ss) return null;
        // Detect "US-only" filter (view=us, or a us_stocks account is selected).
        // When true, show USD primary with INR equivalent below — matches the
        // native currency the user entered values in.
        const isUsOnly = holdings.length > 0 && holdings.every(h => h.country === "US");
        const usdRate = ss.usd_inr_rate || gs?.usd_inr_rate || 0;
        const fmtUSD = (v) => {
          if (v == null || isNaN(v)) return "--";
          const abs = Math.abs(v);
          if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
          if (abs >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
          return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
        };
        // For US-only: prefer the raw USD totals from backend (us_invested_usd,
        // us_value_usd). The combined `total_invested` IS already in INR.
        const invUsd = isUsOnly ? (ss.us_invested_usd ?? (ss.total_invested / (usdRate || 1))) : null;
        const valUsd = isUsOnly ? (ss.us_value_usd ?? (ss.total_value / (usdRate || 1))) : null;
        const pnlUsd = isUsOnly && invUsd != null && valUsd != null ? (valUsd - invUsd) : null;
        return (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-3" data-testid="account-strip">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-[var(--brand)] font-semibold">{filterLabel}</span>
            <span className="text-[10px] text-[var(--text-muted)]">
              {holdings.length} holdings
              {isUsOnly && usdRate > 0 && (
                <span className="ml-2 text-[var(--text-muted)]/70">· ₹{usdRate.toFixed(2)}/$</span>
              )}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-2">
            <div>
              <div className="text-[9px] uppercase text-[var(--text-muted)] tracking-wider">Total Invested</div>
              <div className="font-mono text-lg text-[var(--text-secondary)]" data-testid="strip-total-invested">
                {isUsOnly ? fmtUSD(invUsd) : fmtINR(ss.total_invested)}
              </div>
              {isUsOnly && (
                <div className="text-[10px] text-[var(--text-muted)]/70 font-mono">~{fmtINR(ss.total_invested)}</div>
              )}
            </div>
            <div>
              <div className="text-[9px] uppercase text-[var(--text-muted)] tracking-wider">Current Value</div>
              <div className="font-mono text-lg text-white" data-testid="strip-total-value">
                {isUsOnly ? fmtUSD(valUsd) : fmtINR(ss.total_value)}
              </div>
              {isUsOnly && (
                <div className="text-[10px] text-[var(--text-muted)]/70 font-mono">~{fmtINR(ss.total_value)}</div>
              )}
            </div>
            <div>
              <div className="text-[9px] uppercase text-[var(--text-muted)] tracking-wider">Total P&L</div>
              <div className={`font-mono text-lg ${ss.total_pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                {isUsOnly ? `${(pnlUsd ?? 0) >= 0 ? "+" : ""}${fmtUSD(pnlUsd)}` : `${ss.total_pnl >= 0 ? "+" : ""}${fmtINR(ss.total_pnl)}`}
              </div>
              {isUsOnly && (
                <div className={`text-[10px] font-mono ${ss.total_pnl >= 0 ? "text-[var(--positive)]/70" : "text-[var(--negative)]/70"}`}>~{ss.total_pnl >= 0 ? "+" : ""}{fmtINR(ss.total_pnl)}</div>
              )}
            </div>
            <div>
              <div className="text-[9px] uppercase text-[var(--text-muted)] tracking-wider">Return %</div>
              <div className={`font-mono text-lg ${ss.total_pnl_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>{ss.total_pnl_pct >= 0 ? "+" : ""}{ss.total_pnl_pct}%</div>
            </div>
            <div>
              <div className="text-[9px] uppercase text-[var(--text-muted)] tracking-wider">Holdings</div>
              <div className="font-mono text-lg text-white">{ss.total_stocks}</div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Holdings Table — collapsible (closed by default) */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden" data-testid="holdings-table">
        {/* Totals Header — also acts as collapse toggle */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setHoldingsOpen(o => !o)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setHoldingsOpen(o => !o); } }}
          className="w-full p-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-3 hover:bg-white/[0.02] transition-colors text-left cursor-pointer select-none"
          data-testid="holdings-toggle"
          aria-expanded={holdingsOpen}
        >
          <div className="flex items-center gap-2">
            {holdingsOpen ? <ChevronDown size={14} className="text-[var(--brand)]" /> : <ChevronRight size={14} className="text-[var(--brand)]" />}
            <span className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">Holdings ({holdings.length})</span>
            {!holdingsOpen && <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Click to expand</span>}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {holdings.length > 0 && (() => {
              const isUsOnly = holdings.every(h => h.country === "US");
              // Combined holdings contain INR and USD rows. Convert USD cost and
              // value before totaling; otherwise $172k was being treated as only
              // ₹172k and Combined appeared identical to India.
              const totals = holdingsMetricsInInr(holdings, gs?.usd_inr_rate);
              const totalInvested = isUsOnly
                ? holdings.reduce((sum, h) => sum + holdingInvestedValue(h), 0)
                : totals.total_invested;
              const totalValue = isUsOnly
                ? holdings.reduce((sum, h) => sum + Number(h.current_value || 0), 0)
                : totals.total_value;
              const totalPnl = totalValue - totalInvested;
              const totalPnlPct = totalInvested ? ((totalPnl / totalInvested) * 100) : 0;
              const fmt = isUsOnly
                ? (v) => v == null || isNaN(v) ? "--" : (Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : Math.abs(v) >= 1e3 ? `$${(v / 1e3).toFixed(2)}K` : `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`)
                : fmtINR;
              return (
                <div className="flex gap-5 text-xs" data-testid="holdings-totals">
                  <span className="text-[var(--text-muted)]">Invested: <span className="font-mono text-[var(--text-secondary)]">{fmt(totalInvested)}</span></span>
                  <span className="text-[var(--text-muted)]">Value: <span className="font-mono text-white">{fmt(totalValue)}</span></span>
                  <span className="text-[var(--text-muted)]">P&L: <span className={`font-mono ${totalPnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>{totalPnl >= 0 ? "+" : ""}{fmt(totalPnl)} ({totalPnlPct >= 0 ? "+" : ""}{totalPnlPct.toFixed(1)}%)</span></span>
                </div>
              );
            })()}
            {holdings.length > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); downloadHoldingsCSV(sortedHoldings, { view, filterLabel, holdingScores, dividendYields }); }}
                onKeyDown={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-[var(--brand)]/50 transition-colors text-[11px] uppercase tracking-wider text-[var(--text-secondary)] hover:text-white font-medium"
                title={`Export ${holdings.length} ${filterLabel || "all"} holdings as CSV`}
                data-testid="holdings-export-csv"
              >
                <Download size={12} />
                Export CSV
              </button>
            )}
          </div>
        </div>
        {holdingsOpen && (
          <div className="overflow-x-auto" data-testid="holdings-table-body">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <SortHeader label="Stock" sortKey="tradingsymbol" current={sortKey} dir={sortDir} onClick={toggleSort} align="left" pl />
                  <SortHeader label="Account" sortKey="account" current={sortKey} dir={sortDir} onClick={toggleSort} align="left" />
                  <SortHeader label="Sector" sortKey="sector" current={sortKey} dir={sortDir} onClick={toggleSort} align="left" />
                  <SortHeader label="Score" sortKey="score" current={sortKey} dir={sortDir} onClick={toggleSort} align="center" />
                  <SortHeader label="Qty" sortKey="quantity" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Avg" sortKey="average_price" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="CMP" sortKey="last_price" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Day +/-" sortKey="day_change_pct" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="P&L" sortKey="pnl" current={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Value" sortKey="current_value" current={sortKey} dir={sortDir} onClick={toggleSort} pr />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedHoldings.map((h, i) => { const sc = holdingScores[h.tradingsymbol]; const dy = dividendYields[h.tradingsymbol]; return (
                  <TableRow key={`${h.tradingsymbol}-${h.account_id || i}`} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors" onClick={() => onStockClick?.(h.tradingsymbol)} data-testid={`row-${h.tradingsymbol}`}>
                    <TableCell className="pl-4"><div className="flex items-center gap-2"><span className="font-mono text-sm font-medium text-white">{h.tradingsymbol}</span><span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${h.country === "US" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"}`}>{h.country || "IN"}</span>{dy && dy.yield_pct > 0 && (<span title={`TTM dividend ₹${dy.ttm_dividend} per share`} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${dy.yield_pct >= 3 ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-[var(--text-muted)] border-white/10"}`} data-testid={`yield-${h.tradingsymbol}`}>{dy.yield_pct.toFixed(1)}%</span>)}</div></TableCell>
                    <TableCell><span className="text-xs text-[var(--text-muted)]">{h.account_name || h.broker}</span></TableCell>
                    <TableCell><span className="text-xs text-[var(--text-secondary)]">{h.sector}</span></TableCell>
                    <TableCell className="text-center">{sc ? <MiniSnowflake sc={sc} /> : <span className="text-[10px] text-[var(--text-muted)]">-</span>}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[var(--text-secondary)]">{h.quantity}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[var(--text-secondary)]">{h.average_price > 0 ? (h.country === "US" ? `$${fmtNum(h.average_price)}` : fmtNum(h.average_price)) : "--"}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-white">{h.last_price > 0 ? (h.country === "US" ? `$${fmtNum(h.last_price)}` : fmtNum(h.last_price)) : "--"}</TableCell>
                    <TableCell className="text-right">
                      {h.day_change_pct !== null && h.day_change_pct !== undefined ? (
                        <div className="flex flex-col items-end gap-0">
                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${(h.day_change_pct ?? 0) >= 0 ? "text-[var(--positive)] bg-[var(--positive)]/10" : "text-[var(--negative)] bg-[var(--negative)]/10"}`} data-testid={`day-chg-${h.tradingsymbol}`}>
                            {(h.day_change_pct ?? 0) >= 0 ? "+" : ""}{(h.day_change_pct ?? 0).toFixed(2)}%
                          </span>
                          {h.day_change ? (
                            <span className={`font-mono text-[10px] mt-0.5 ${(h.day_change_pct ?? 0) >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"} opacity-60`}>
                              {fmtSignedMoney(h.day_change, h.country)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--text-muted)]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right"><span className={`font-mono text-sm ${h.pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>{h.pnl !== 0 ? fmtSignedMoney(h.pnl, h.country) : "--"}</span></TableCell>
                    <TableCell className="text-right pr-4 font-mono text-sm text-white" data-testid={`value-${h.tradingsymbol}`}>
                      {h.current_value > 0 ? (
                        <div className="flex flex-col items-end gap-0">
                          <span>{fmtMoney(h.current_value, h.country)}</span>
                          {(() => {
                            const inv = h.invested_value || (h.average_price * h.quantity) || 0;
                            return inv > 0 ? (
                              <span className="text-[9px] text-[var(--text-muted)] mt-0.5">
                                inv {fmtMoney(inv, h.country)}
                              </span>
                            ) : null;
                          })()}
                          {h.country === "US" && gs?.usd_inr_rate > 0 && (
                            <span className="text-[9px] text-[var(--text-muted)]/70" title={`@ ₹${gs.usd_inr_rate.toFixed(2)}/$ (accounting spot rate)`}>
                              ~{fmtINR(h.current_value * gs.usd_inr_rate)}
                            </span>
                          )}
                        </div>
                      ) : "--"}
                    </TableCell>
                  </TableRow>); })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Rule Violations Banner */}
      {ruleViolations?.summary?.total_violations > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-md p-3 flex items-center gap-3" data-testid="rule-violations-banner">
          <ShieldAlert size={16} className="text-amber-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-sm text-amber-300 font-medium">{ruleViolations.summary.total_violations} rule violations</span>
            <span className="text-xs text-[var(--text-muted)] ml-2">
              {ruleViolations.summary.critical > 0 && <span className="text-red-400 mr-2">{ruleViolations.summary.critical} critical</span>}
              {ruleViolations.summary.warnings > 0 && <span className="text-amber-400 mr-2">{ruleViolations.summary.warnings} warnings</span>}
              {ruleViolations.summary.info > 0 && <span className="text-blue-400">{ruleViolations.summary.info} info</span>}
            </span>
          </div>
          <div className="flex gap-1 flex-shrink-0 flex-wrap">
            {(ruleViolations.violations || []).slice(0, 3).map((v, i) => (
              <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded-sm border ${v.severity === "critical" ? "bg-red-500/10 text-red-400 border-red-500/20" : v.severity === "warning" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-blue-500/10 text-blue-400 border-blue-500/20"}`}>{v.symbol || v.sector || v.category || v.rule}</span>
            ))}
            {(ruleViolations.violations || []).length > 3 && <span className="text-[9px] text-[var(--text-muted)]">+{ruleViolations.violations.length - 3} more</span>}
          </div>
        </div>
      )}

      {/* Top Movers — placed above Performance for at-a-glance visibility */}
      <TopMoversCard />

      {/* Main content: Watchlist sidebar + Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: Watchlist */}
        <div className="lg:col-span-2 bg-[var(--bg-surface)] border border-white/10 rounded-md p-3" data-testid="watchlist-panel">
          <div className="flex items-center gap-1.5 mb-3">
            <Star size={14} className="text-amber-400" />
            <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">Watchlist</span>
          </div>
          {watchlist.length === 0 ? (
            <p className="text-[10px] text-[var(--text-muted)]">Search stocks to add to watchlist</p>
          ) : (
            <div className="space-y-1">
              {watchlist.map(w => (
                <div key={w.symbol} className="flex items-center justify-between p-1.5 rounded hover:bg-white/[0.03] cursor-pointer group" onClick={() => onStockClick?.(w.symbol)}>
                  <div>
                    <div className="font-mono text-[11px] text-white">{w.symbol}</div>
                    {w.last_price > 0 && <div className="font-mono text-[9px] text-[var(--text-muted)]">{w.last_price.toLocaleString()}</div>}
                  </div>
                  <button onClick={async (e) => { e.stopPropagation(); await removeFromWatchlist(w.symbol); loadWatchlist(); }} className="text-[var(--text-muted)] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-0.5">
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Center: Performance Chart */}
        <div className="lg:col-span-7 bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="perf-chart">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">Performance</span>
              {dayPnl && perfMode === "value" && (
                <span className={`font-mono text-xs font-bold ${dayPnl.pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                  {dayPnl.pnl >= 0 ? "+" : ""}{fmtINR(dayPnl.pnl)} ({dayPnl.pnl >= 0 ? "+" : ""}{dayPnl.pct}%)
                </span>
              )}
              {perfMode === "price_only" && perfSummary && (
                <span className={`font-mono text-xs font-bold ${perfSummary.total_return_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`} data-testid="perf-price-only-total">
                  {perfSummary.total_return_pct >= 0 ? "+" : ""}{perfSummary.total_return_pct.toFixed(2)}% (price-only)
                </span>
              )}
            </div>
            <div className="flex gap-1.5 items-center flex-wrap">
              {/* Mode toggle */}
              <div className="flex gap-0 bg-[var(--bg-base)] rounded p-0.5">
                <button
                  onClick={() => setPerfMode("value")}
                  className={`px-2 py-1 text-[10px] rounded transition-colors ${perfMode === "value" ? "bg-[var(--brand)]/15 text-[var(--brand)]" : "text-[var(--text-muted)] hover:text-white"}`}
                  data-testid="perf-mode-value"
                  title="Total wallet value (includes buy/sell flows)"
                >
                  Value
                </button>
                <button
                  onClick={() => setPerfMode("price_only")}
                  className={`px-2 py-1 text-[10px] rounded transition-colors ${perfMode === "price_only" ? "bg-[var(--brand)]/15 text-[var(--brand)]" : "text-[var(--text-muted)] hover:text-white"}`}
                  data-testid="perf-mode-price-only"
                  title="Pure stock performance — ignores buy/sell flows"
                >
                  Price-only %
                </button>
              </div>
              <div className="flex gap-1">
                {["1D", "1W", "1M", "3M", "6M", "1Y"].map(r => (
                  <button key={r} onClick={() => setPerfRange(r)} className={`px-2 py-1 text-[10px] rounded-md font-medium transition-colors ${perfRange === r ? "bg-cyan-500/20 text-cyan-400" : "text-[var(--text-muted)] hover:text-white"}`} data-testid={`perf-range-${r}`}>{r}</button>
                ))}
              </div>
              {perfMode === "price_only" && perf.some(p => p.nifty_pct !== undefined) && (
                <button
                  onClick={() => setShowNifty(v => !v)}
                  className={`px-2 py-1 text-[10px] rounded-md font-medium transition-colors flex items-center gap-1.5 ${showNifty ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "text-[var(--text-muted)] border border-white/10 hover:text-white"}`}
                  title="Toggle Nifty 50 benchmark overlay"
                  data-testid="perf-nifty-toggle"
                >
                  <span className="inline-block w-3 border-t border-dashed border-amber-400" />
                  Nifty 50
                </button>
              )}
            </div>
          </div>
          {perf.length <= 1 ? (
            <div className="flex items-center justify-center h-[180px] text-xs text-[var(--text-muted)]">Snapshots will build up over time.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={perf} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>{filteredPerfAccounts.map((a, i) => (<linearGradient key={a.name} id={`gPerf${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={ACCT_COLORS[i % ACCT_COLORS.length]} stopOpacity={0.15} /><stop offset="95%" stopColor={ACCT_COLORS[i % ACCT_COLORS.length]} stopOpacity={0} /></linearGradient>))}</defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={v => v.includes(" ") ? v.split(" ")[1] : v.slice(5)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={perfMode === "price_only" ? (v => `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`) : (v => v >= 1e7 ? `${(v/1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v/1e5).toFixed(1)}L` : `${(v/1e3).toFixed(0)}K`)} axisLine={false} tickLine={false} width={perfMode === "price_only" ? 56 : 48} />
                  <Tooltip content={<PerfTooltip accounts={filteredPerfAccounts} mode={perfMode} showNifty={showNifty} />} />
                  {perfMode === "price_only" && <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />}
                  {filteredPerfAccounts.map((a, i) => (<Area key={a.name} type="monotone" dataKey={a.name} stroke={ACCT_COLORS[i % ACCT_COLORS.length]} strokeWidth={1.5} fill={`url(#gPerf${i})`} dot={false} />))}
                  {perfMode === "price_only" && showNifty && perf.some(p => p.nifty_pct !== undefined) && (
                    <Line type="monotone" dataKey="nifty_pct" stroke="#F59E0B" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
              {perf.length > 2 && perfMode === "value" && (
                <div className="mt-1">
                  <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 ml-1">Daily Change</div>
                  <ResponsiveContainer width="100%" height={50}>
                    <BarChart data={perf} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                      <XAxis dataKey="date" tick={false} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "#64748B", fontSize: 8 }} tickFormatter={v => v >= 1e5 ? `${(v/1e5).toFixed(0)}L` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : v.toFixed(0)} axisLine={false} tickLine={false} width={48} />
                      <Tooltip content={<DailyChangeTooltip />} />
                      <ReferenceLine y={0} stroke="rgba(255,255,255,0.1)" />
                      <Bar dataKey="daily_change" radius={[2, 2, 0, 0]}>{perf.map((e, i) => (<Cell key={i} fill={e.daily_change >= 0 ? "#10B981" : "#EF4444"} fillOpacity={0.7} />))}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
          <div className="flex flex-wrap gap-3 mt-1.5 ml-1">
            {filteredPerfAccounts.map((a, i) => (<span key={a.name} className="flex items-center gap-1.5 text-[9px] text-[var(--text-muted)]"><span className="w-2 h-0.5 rounded-full inline-block" style={{ background: ACCT_COLORS[i % ACCT_COLORS.length] }} />{a.name}</span>))}
          </div>
        </div>

        {/* Right: Sector Pie */}
        <div className="lg:col-span-3 bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="sector-chart">
          <div className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-2">Sector Allocation</div>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={sectors.slice(0, 8)} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} dataKey="percentage" nameKey="sector" stroke="none">
                {sectors.slice(0, 8).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip content={<SectorTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1">
            {sectors.slice(0, 8).map((sc, i) => (
              <div key={sc.sector} className="flex items-center gap-1 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                <span className="text-[var(--text-secondary)] truncate">{sc.sector}</span>
                <span className="font-mono text-[var(--text-muted)] ml-auto">{sc.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Briefings Row — 3 cards side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ai-briefings-row">
        <MorningSummaryCard />
        <EveningWrapCard />
        <WeekendReviewCard />
      </div>

      {/* Closing Bell — full-width digest, today's biggest movers in your portfolio */}
      <ClosingBellCard />

      {/* Upcoming corporate actions — dividends, splits, bonuses, rights */}
      <CorporateActionsCard />

      {/* Total annual passive income from dividends + monthly forecast */}
      <PassiveIncomeCard />

      {/* AI Chat + Health Score Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-3 bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="score-panel">
          <div className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-3">Health Score</div>
          {score && <ScoreRing score={score.total_score} max={score.max_score} breakdown={score.breakdown} />}
        </div>
        <div className="lg:col-span-5" data-testid="ai-panel">
          <PortfolioChat />
          {analysis && (
            <div className="mt-3 bg-[var(--bg-surface)] border border-white/10 rounded-md p-4">
              <div className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-2">Portfolio Analysis</div>
              <div className="text-sm text-[var(--text-secondary)] leading-relaxed max-h-[200px] overflow-y-auto whitespace-pre-wrap" data-testid="ai-result">{analysis}</div>
            </div>
          )}
          <div className="hidden">
            <Button onClick={handleAnalyze} disabled={analyzing} data-testid="ai-analyze-btn">Analyze</Button>
          </div>
        </div>
        <div className="lg:col-span-4">
          <MarketPulsePanel />
        </div>
      </div>

      {/* Buy/Sell Zones + Portfolio News */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {zones && (zones.buy_zone?.length > 0 || zones.sell_zone?.length > 0) && (
          <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="zones-panel">
            <div className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-3">Action Zones</div>
            <div className="max-h-[350px] overflow-y-auto pr-1 space-y-3">
            {zones.buy_zone?.length > 0 && (
              <div className="mb-3"><div className="flex items-center gap-1.5 mb-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" /><span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Buy Zone ({zones.buy_zone.length})</span></div>
                <div className="space-y-1">{zones.buy_zone.map((z, i) => (<div key={i} className="flex items-center justify-between p-2 bg-emerald-500/5 rounded border border-emerald-500/10 cursor-pointer hover:border-emerald-500/25 transition-colors" onClick={() => onStockClick?.(z.symbol)} data-testid={`buy-zone-${z.symbol}`}><div><span className="font-mono text-xs text-white">{z.symbol}</span><span className="text-[9px] text-[var(--text-muted)] ml-2">{z.account_name}</span><div className="text-[9px] text-emerald-400/70">{z.reason}</div></div><div className="text-right"><div className="font-mono text-[10px] text-white">{fmtINR(z.value)}</div><div className={`font-mono text-[9px] ${z.pnl_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>{z.pnl_pct >= 0 ? "+" : ""}{z.pnl_pct?.toFixed(1)}%</div></div></div>))}</div></div>
            )}
            {zones.sell_zone?.length > 0 && (
              <div><div className="flex items-center gap-1.5 mb-1.5"><span className="w-2 h-2 rounded-full bg-orange-400" /><span className="text-[10px] font-semibold text-orange-400 uppercase tracking-wider">Reduce ({zones.sell_zone.length})</span></div>
                <div className="space-y-1">{zones.sell_zone.map((z, i) => (<div key={i} className="flex items-center justify-between p-2 bg-orange-500/5 rounded border border-orange-500/10 cursor-pointer hover:border-orange-500/25 transition-colors" onClick={() => onStockClick?.(z.symbol)} data-testid={`sell-zone-${z.symbol}`}><div><span className="font-mono text-xs text-white">{z.symbol}</span><span className="text-[9px] text-[var(--text-muted)] ml-2">{z.account_name}</span><div className="text-[9px] text-orange-400/70">{z.reason}</div></div><div className="text-right"><div className="font-mono text-[10px] text-white">{fmtINR(z.value)}</div><div className={`font-mono text-[9px] text-[var(--positive)]`}>+{z.pnl_pct?.toFixed(1)}%</div></div></div>))}</div></div>
            )}
            </div>
          </div>
        )}
        {portfolioNews.length > 0 && (
          <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="portfolio-news">
            <div className="flex items-center gap-2 mb-3"><Newspaper size={14} className="text-cyan-400" /><span className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">Portfolio News</span></div>
            <div className="space-y-1 max-h-[350px] overflow-y-auto">
              {portfolioNews.map((n, i) => (
                <a key={i} href={n.link} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 p-2 bg-[var(--bg-base)] rounded border border-white/5 hover:border-white/15 transition-colors group">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5"><span className="text-[9px] font-mono px-1 py-0.5 rounded-sm bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">{n.symbol}</span>{n.publisher && <span className="text-[9px] text-[var(--text-muted)]">{n.publisher}</span>}</div>
                    <div className="text-[11px] text-white group-hover:text-[var(--brand)] transition-colors line-clamp-2">{n.title}</div>
                  </div>
                  <ExternalLink size={10} className="text-[var(--text-muted)] flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {pnlPopupOpen && (
        <PnlBreakdownModal breakdown={pnlBreakdown} onClose={() => setPnlPopupOpen(false)} />
      )}
    </div>
  );
}

function PnlBreakdownModal({ breakdown, onClose }) {
  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="pnl-breakdown-modal">
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "Chivo" }}>Total P&L — Broker-wise Breakdown</h2>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">How your ₹{breakdown?.total_pnl ? (breakdown.total_pnl / 1e7).toFixed(2) + "Cr" : "--"} P&L is split across accounts</p>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-white p-1 rounded" data-testid="pnl-breakdown-close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {!breakdown ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-[var(--brand)]" /></div>
          ) : breakdown.error ? (
            <div className="text-center text-sm text-red-400 py-6">Failed to load breakdown.</div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 pb-3 border-b border-white/10">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">Total Invested</div>
                  <div className="font-mono text-base text-[var(--text-secondary)]">{fmtINR(breakdown.total_invested)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">Current Value</div>
                  <div className="font-mono text-base text-white">{fmtINR(breakdown.total_current_value)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">Total P&L</div>
                  <div className={`font-mono text-base ${breakdown.total_pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                    {breakdown.total_pnl >= 0 ? "+" : ""}{fmtINR(breakdown.total_pnl)} ({breakdown.total_pnl >= 0 ? "+" : ""}{breakdown.total_pnl_pct}%)
                  </div>
                </div>
              </div>
              {/* Per account */}
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold pl-0">Account</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">Invested</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">Current</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">P&L</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">Return %</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right pr-0">Holdings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(breakdown.accounts || []).map((a, i) => {
                    const contribution = breakdown.total_pnl ? ((a.pnl / breakdown.total_pnl) * 100).toFixed(1) : 0;
                    return (
                      <TableRow key={i} className="border-b border-white/5" data-testid={`pnl-row-${a.account_name}`}>
                        <TableCell className="pl-0 py-2.5">
                          <div className="font-mono text-sm text-white">{a.account_name}</div>
                          <div className="text-[10px] text-[var(--text-muted)] uppercase">{a.broker}{a.pnl >= 0 ? ` · ${contribution}% of gains` : ""}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-[var(--text-secondary)]">{fmtINR(a.invested)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-white">{fmtINR(a.current_value)}</TableCell>
                        <TableCell className={`text-right font-mono text-xs ${a.pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                          {a.pnl >= 0 ? "+" : ""}{fmtINR(a.pnl)}
                        </TableCell>
                        <TableCell className={`text-right font-mono text-xs ${a.pnl_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                          {a.pnl_pct >= 0 ? "+" : ""}{a.pnl_pct}%
                        </TableCell>
                        <TableCell className="text-right pr-0 font-mono text-xs text-[var(--text-muted)]">{a.holdings_count}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <p className="text-[10px] text-[var(--text-muted)] pt-2">
                * US holdings are converted to INR using the current accounting USD/INR rate. Invested uses stored cost-basis or avg × qty where cost-basis is missing.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniSnowflake({ sc }) {
  const dims = [sc.value, sc.future, sc.past, sc.health, sc.dividend];
  const avg = sc.avg_score;
  const color = avg >= 7 ? "#10B981" : avg >= 5 ? "#F59E0B" : "#EF4444";
  const cx = 16, cy = 16, r = 13;
  const angles = dims.map((_, i) => (Math.PI * 2 * i) / 5 - Math.PI / 2);
  const pts = dims.map((d, i) => { const scale = (d / 10) * r; return `${cx + scale * Math.cos(angles[i])},${cy + scale * Math.sin(angles[i])}`; }).join(" ");
  const outerPts = angles.map(a => `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`).join(" ");
  return (
    <div className="flex items-center gap-1.5 justify-center" data-testid="mini-snowflake">
      <svg width="32" height="32" viewBox="0 0 32 32"><polygon points={outerPts} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" /><polygon points={pts} fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1" /></svg>
      <div className="text-right leading-none"><div className="font-mono text-xs font-bold" style={{ color }}>{avg}</div><div className="text-[8px] text-[var(--text-muted)]">{sc.action}</div></div>
    </div>
  );
}

function KPICard({ label, value, sub, icon, positive, testId, live, onClick }) {
  const clickable = !!onClick;
  const tone = positive === false ? "red" : positive === true ? "green" : /equity|fund|stock|portfolio|invest/i.test(label) ? "investment" : "cyan";
  return (
    <div
      data-tone={tone}
      className={`semantic-card border rounded-md p-3 ${clickable ? "cursor-pointer transition-colors" : ""}`}
      data-testid={testId}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[9px] tracking-[0.1em] uppercase text-[var(--text-muted)] font-semibold flex items-center gap-1.5">{label}{live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" title="Live" />}</span>
        <span className={positive === false ? "text-[var(--negative)]" : positive === true ? "text-[var(--positive)]" : "text-[var(--brand)]"}>{icon}</span>
      </div>
      <div className="semantic-value font-mono text-xl font-medium tracking-tight">{value}</div>
      {sub && <span className={`font-mono text-[10px] ${positive === false ? "text-[var(--negative)]" : positive === true ? "text-[var(--positive)]" : "text-[var(--text-muted)]"}`}>{sub}</span>}
    </div>
  );
}

function SortHeader({ label, sortKey, current, dir, onClick, pr, pl, align = "right" }) {
  const active = current === sortKey;
  const alignCls = align === "left" ? "text-left" : align === "center" ? "text-center" : "text-right";
  const innerCls = align === "left" ? "" : align === "center" ? "justify-center" : "";
  return (<TableHead className={`${alignCls} cursor-pointer select-none group ${pr ? "pr-4" : ""} ${pl ? "pl-4" : ""}`} onClick={() => onClick(sortKey)} data-testid={`sort-${sortKey}`}>
    <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold transition-colors ${innerCls} ${active ? "text-[var(--brand)]" : "text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]"}`}>{label}{active ? (dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-40" />}</span>
  </TableHead>);
}

function ScoreRing({ score, max, breakdown }) {
  const pct = Math.round((score / max) * 100);
  const color = pct >= 75 ? "var(--positive)" : pct >= 50 ? "var(--warning)" : "var(--negative)";
  const r = 45, c = 2 * Math.PI * r, d = c - (pct / 100) * c;
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <svg width="96" height="96" viewBox="0 0 96 96"><circle cx="48" cy="48" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" /><circle cx="48" cy="48" r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={d} transform="rotate(-90 48 48)" style={{ transition: "stroke-dashoffset 1s" }} /></svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="font-mono text-xl font-medium" style={{ color }}>{score}</span><span className="text-[9px] text-[var(--text-muted)]">/{max}</span></div>
      </div>
      {breakdown && (<div className="w-full mt-2 space-y-1">{Object.entries(breakdown).map(([k, v]) => (<div key={k}><div className="flex justify-between text-[9px] mb-0.5"><span className="text-[var(--text-secondary)] capitalize">{k.replace(/_/g, " ")}</span><span className="font-mono text-[var(--text-muted)]">{v.score}/{v.max}</span></div><div className="h-1 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(v.score/v.max)*100}%`, background: (v.score/v.max) > 0.7 ? "var(--positive)" : (v.score/v.max) > 0.4 ? "var(--warning)" : "var(--negative)", transition: "width 0.7s" }} /></div></div>))}</div>)}
    </div>
  );
}

function PerfTooltip({ active, payload, label, mode }) {
  if (!active || !payload?.length) return null;
  const isPct = mode === "price_only";
  const fmt = (v) => isPct ? `${v >= 0 ? "+" : ""}${(v ?? 0).toFixed(2)}%` : fmtINR(v);
  // Separate nifty overlay so it doesn't skew the "Total" row or confuse colors
  const rows = payload.filter(p => p.dataKey !== "nifty_pct");
  const nifty = payload.find(p => p.dataKey === "nifty_pct");
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-xs shadow-xl min-w-[160px]">
      <div className="text-[var(--text-muted)] font-mono mb-1">{label}</div>
      {rows.map((p, i) => (<div key={i} className="flex items-center justify-between gap-3"><span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: p.stroke || p.color }} /><span className="text-[var(--text-secondary)] truncate max-w-[100px]">{p.name === "total" ? "Your Portfolio" : p.name}</span></span><span className={`font-mono ${isPct ? (p.value >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]") : "text-white"}`}>{fmt(p.value)}</span></div>))}
      {!isPct && rows.length > 0 && (<div className="mt-1 pt-1 border-t border-white/10 flex justify-between"><span className="text-[var(--text-muted)]">Total</span><span className="font-mono text-white font-semibold">{fmtINR(rows.reduce((s, p) => s + (p.value || 0), 0))}</span></div>)}
      {nifty && (
        <div className="mt-1 pt-1 border-t border-white/10 flex items-center justify-between gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-0.5 rounded-full bg-amber-400 inline-block" style={{ borderTop: "1px dashed #F59E0B", background: "transparent" }} /><span className="text-amber-300">Nifty 50</span></span>
          <span className={`font-mono ${nifty.value >= 0 ? "text-emerald-300" : "text-red-300"}`}>{nifty.value >= 0 ? "+" : ""}{(nifty.value ?? 0).toFixed(2)}%</span>
        </div>
      )}
    </div>
  );
}

function DailyChangeTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (<div className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-xs shadow-xl"><div className="text-[var(--text-muted)] font-mono mb-0.5">{label}</div><div className={`font-mono font-semibold ${val >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>{val >= 0 ? "+" : ""}{fmtINR(val)}</div></div>);
}

function SectorTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (<div className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-xs shadow-xl"><div className="text-white font-medium">{d.sector}</div><div className="font-mono text-[var(--text-secondary)]">{d.percentage}%</div></div>);
}

function fmtINR(v) { if (!v && v !== 0) return "--"; const abs = Math.abs(v); if (abs >= 1e7) return `₹${(v/1e7).toFixed(2)}Cr`; if (abs >= 1e5) return `₹${(v/1e5).toFixed(2)}L`; return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`; }
function fmtNum(v) { if (!v && v !== 0) return "--"; return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtMoney(v, country) {
  if (v == null || v === "" || (typeof v === "number" && isNaN(v))) return "--";
  if (country === "US") {
    const abs = Math.abs(v);
    if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
    return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
  return fmtINR(v);
}
function fmtSignedMoney(v, country) {
  if (v == null || v === "" || (typeof v === "number" && isNaN(v))) return "--";
  const sign = v >= 0 ? "+" : "−";
  return `${sign}${fmtMoney(Math.abs(v), country)}`;
}


// ---- CSV Export -----------------------------------------------------------
// Escapes a single field per RFC4180: wrap in quotes if it contains comma,
// quote, newline or leading/trailing whitespace; double up internal quotes.
function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s) || /^\s|\s$/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildHoldingsCSV(rows, { holdingScores = {}, dividendYields = {} } = {}) {
  const headers = [
    "Stock",
    "Country",
    "Exchange",
    "Account",
    "Broker",
    "Sector",
    "Asset Class",
    "Score",
    "Quantity",
    "Avg Price",
    "CMP",
    "Currency",
    "Day Change %",
    "Day Change",
    "Invested Value",
    "Current Value",
    "P&L",
    "P&L %",
    "TTM Dividend Yield %",
    "TTM Dividend / Share",
  ];
  const lines = [headers.join(",")];
  for (const h of rows) {
    const inv = h.invested_value || (h.average_price * h.quantity) || 0;
    const sc = holdingScores[h.tradingsymbol];
    const dy = dividendYields[h.tradingsymbol];
    const currency = h.country === "US" ? "USD" : "INR";
    const row = [
      h.tradingsymbol || "",
      h.country || "IN",
      h.exchange || "",
      h.account_name || "",
      h.broker || h.brokerage || "",
      h.sector || "",
      h.asset_class || "",
      sc && sc.avg_score != null ? sc.avg_score.toFixed(2) : "",
      h.quantity ?? "",
      h.average_price ?? "",
      h.last_price ?? "",
      currency,
      h.day_change_pct != null ? h.day_change_pct.toFixed(2) : "",
      h.day_change != null ? h.day_change.toFixed(2) : "",
      inv ? inv.toFixed(2) : "",
      h.current_value != null ? h.current_value.toFixed(2) : "",
      h.pnl != null ? h.pnl.toFixed(2) : "",
      h.pnl_pct != null ? h.pnl_pct : "",
      dy && dy.yield_pct != null ? dy.yield_pct.toFixed(2) : "",
      dy && dy.ttm_dividend != null ? dy.ttm_dividend : "",
    ].map(csvEscape);
    lines.push(row.join(","));
  }
  return lines.join("\n");
}

export function downloadHoldingsCSV(rows, { view = "all", filterLabel = "", holdingScores, dividendYields } = {}) {
  if (!rows || rows.length === 0) return;
  const csv = buildHoldingsCSV(rows, { holdingScores, dividendYields });
  // BOM so Excel opens UTF-8 (₹, $) correctly
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const date = new Date().toISOString().slice(0, 10);
  const slug = (filterLabel || view || "all").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "all";
  const link = document.createElement("a");
  link.href = url;
  link.download = `capital360-holdings-${slug}-${date}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
